/**
 * Estimated-earnings helpers for the route preview.
 *
 * The figure is a fleet capacity projection: each van (≈ one technician)
 * completes a fixed number of RO's per day, and total earnings scale linearly
 * with the number of vans on the route board. Per RO we credit labor gross,
 * parts gross, and (for offset OEMs like Ford) a manufacturer offset.
 *
 * All inputs are tenant-configurable via `TenantRouteSettings.earnings_params`;
 * the defaults below apply when a tenant hasn't customized them. The van count
 * is live route-board context (technician count), not a stored param.
 */
import type { EarningsParams } from '@/types/technicianRoutes';

export const DEFAULT_EARNINGS_PARAMS: EarningsParams = {
  labor_rate_per_hour: 220,
  hours_per_recall: 0.5,
  gross_percentage: 0.75,
  parts_sales_percentage: 0.3,
  parts_gross_percentage: 0.4,
  offset_rate_per_recall: 0,
  daily_ros_per_van: 5,
  working_days_per_month: 20
};

/** Conservative manufacturer offset default ($/RO) for a tenant's OEM(s). Ford only. */
export const DEFAULT_FORD_OFFSET = 120;

/** Resolve the offset default from a tenant's OEM(s): Ford gets the offset, others 0. */
export function defaultOffsetForOems(oems?: string[], oem?: string): number {
  const list = oems?.length ? oems : oem ? [oem] : [];
  return list.some(o => o.toLowerCase() === 'ford') ? DEFAULT_FORD_OFFSET : 0;
}

/**
 * Resolve a complete params object, falling back to defaults per-field.
 * `offsetDefault` lets the caller seed the OEM-derived offset (Ford 120 / others 0)
 * when the tenant hasn't explicitly set one. Idempotent: re-resolving an already
 * complete object keeps its explicit values.
 */
export function resolveEarningsParams(
  params?: Partial<EarningsParams>,
  offsetDefault = 0
): EarningsParams {
  return {
    labor_rate_per_hour: params?.labor_rate_per_hour ?? DEFAULT_EARNINGS_PARAMS.labor_rate_per_hour,
    hours_per_recall: params?.hours_per_recall ?? DEFAULT_EARNINGS_PARAMS.hours_per_recall,
    gross_percentage: params?.gross_percentage ?? DEFAULT_EARNINGS_PARAMS.gross_percentage,
    parts_sales_percentage:
      params?.parts_sales_percentage ?? DEFAULT_EARNINGS_PARAMS.parts_sales_percentage,
    parts_gross_percentage:
      params?.parts_gross_percentage ?? DEFAULT_EARNINGS_PARAMS.parts_gross_percentage,
    offset_rate_per_recall: params?.offset_rate_per_recall ?? offsetDefault,
    daily_ros_per_van: params?.daily_ros_per_van ?? DEFAULT_EARNINGS_PARAMS.daily_ros_per_van,
    working_days_per_month:
      params?.working_days_per_month ?? DEFAULT_EARNINGS_PARAMS.working_days_per_month
  };
}

/**
 * Earnings estimate for a specific recall count (e.g. today's planned route).
 * Uses the actual number of planned recalls rather than a fleet capacity projection.
 */
export function computeRouteEarnings(totalRecalls: number, params: EarningsParams): number {
  return totalRecalls * computePerRecallValue(params);
}

/** Net P&L credited per RO: labor gross + parts gross + manufacturer offset. */
export function computePerRecallValue(p: EarningsParams): number {
  const laborSales = p.hours_per_recall * p.labor_rate_per_hour;
  const laborGross = laborSales * p.gross_percentage;
  const partsGross = laborSales * p.parts_sales_percentage * p.parts_gross_percentage;
  return laborGross + partsGross + p.offset_rate_per_recall;
}

/**
 * Fleet earnings projection for `numVans` vans (= technician routes).
 * Returns monthly and annual figures.
 */
export function computeFleetEarnings(
  numVans: number,
  params: EarningsParams
): { monthly: number; annual: number } {
  const perRO = computePerRecallValue(params);
  const monthly = numVans * params.daily_ros_per_van * params.working_days_per_month * perRO;
  return { monthly, annual: monthly * 12 };
}

/** Format a dollar amount as a whole-dollar currency string. */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}
